import * as React from "react"
import { navigate } from "gatsby"
import {
  ThemeCss,
  Text,
  Theme,
  AriaFormGroupFieldData,
  IconProps,
  CheckIcon,
  LinkButton,
} from "gatsby-interface"
import { MdArrowForward } from "react-icons/md"
import {
  RadioButtonCard,
  RadioButtonCardInput,
  useRadioButtonCardContext,
} from "@modules/ui/components/RadioButtonCard"
import {
  RichFeature,
  TierInformation,
  TierType,
} from "@modules/billing/shared/constants/tiers"
import { billing as billingText } from "@modules/locales/default.json"
import { BillingInterval, MachinePricingTier } from "@modules/graphql/types"
import { useTracker, SegmentEventType } from "@modules/analytics"
import capitalizeStr from "@modules/toolkit/helpers/capitalizeStr"

type TierIconProps = {
  icon?: (props: IconProps) => JSX.Element
  isStaticCard?: boolean
}

function TierIcon({ icon, isStaticCard }: TierIconProps) {
  const { hovered, checked } = useRadioButtonCardContext()
  const Icon = icon

  if (isStaticCard && Icon) {
    return <Icon />
  }

  return !hovered && !checked && Icon ? <Icon /> : null
}

const rootCss: ThemeCss = theme => ({
  paddingTop: theme.space[6],
  paddingBottom: theme.space[6],
  paddingLeft: theme.space[6],
  paddingRight: theme.space[6],
  borderColor: theme.colors.purple[10],
  transition: `border-color .5s`,
})

const isSelectedCss: ThemeCss = theme => ({
  borderColor: theme.colors.purple[60],
})

const isHoveredCss: ThemeCss = theme => ({
  ":hover": {
    borderColor: theme.colors.purple[30],
  },
})

const isStaticCss: ThemeCss = theme => ({
  cursor: `default`,
  boxShadow: `none`,
  textAlign: `center`,
  alignItems: `center`,
  background: theme.colors.purple[5],

  ":hover": {
    borderColor: theme.colors.purple[10],
  },
})

const headerCss: ThemeCss = theme => ({
  display: `flex`,
  alignItems: `flex-end`,
  marginBottom: theme.space[4],
})

const isCenteredCss: ThemeCss = theme => ({
  justifyContent: `center`,
  justifyItems: `center`,
  alignItems: `center`,
})

const pileCss: ThemeCss = theme => ({
  width: `24px`,
  height: `24px`,
  position: `relative`,
  marginRight: theme.space[4],
  transform: `translateY(-10%)`,

  "& > svg": {
    position: `absolute`,
    top: 0,
    left: 0,
  },
})

const labelCss: ThemeCss = theme => ({
  fontSize: theme.fontSizes[3],
  fontWeight: theme.fontWeights.bold,
  letterSpacing: `-0.01em`,
})

const pricingCss: ThemeCss = theme => ({
  color: theme.colors.grey[60],
  fontSize: theme.fontSizes[2],
  marginLeft: `auto`,
  b: {
    color: theme.colors.grey[90],
  },
  small: {
    fontSize: theme.fontSizes[0],
  },
})

const descriptionCss: ThemeCss = theme => ({
  color: theme.colors.grey[60],
  marginTop: 0,
  marginBottom: theme.space[5],
  fontSize: theme.fontSizes[0],
})

const featuresIntroCss: ThemeCss = theme => ({
  display: `block`,
  marginBottom: theme.space[3],
  color: theme.colors.grey[70],
  fontSize: theme.fontSizes[1],
})

const featuresList: ThemeCss = theme => ({
  listStyle: `none`,
  padding: 0,
  margin: 0,
  fontSize: theme.fontSizes[2],
  lineHeight: theme.lineHeights.default,
  color: theme.colors.grey[90],
  display: `grid`,
  marginTop: `auto`,
})

const featureItemCss: ThemeCss = theme => ({
  display: `flex`,
  alignItems: `center`,
  fontSize: theme.fontSizes[2],
  margin: `${theme.space[1]} 0`,
  color: theme.colors.grey[70],
})

const featureIconBox: ThemeCss = theme => ({
  width: `24px`,
  height: `24px`,
  display: `inline-flex`,
  alignItems: `center`,
  justifyContent: `center`,
  marginRight: theme.space[3],
})

const featureIconCss: ThemeCss = theme => ({
  color: theme.colors.grey[60],
})

const checkIconCss: ThemeCss = theme => ({
  color: theme.colors.green[60],
})

const detailsLinkCss: ThemeCss = theme => ({
  fontSize: theme.fontSizes[0],
  textDecoration: `none`,
  marginTop: theme.space[3],
  marginLeft: theme.space[7],
  display: `inline-flex`,
  alignItems: `center`,
  alignSelf: `start`,

  svg: {
    marginLeft: theme.space[3],
  },
})

const contactSalesCss: ThemeCss = theme => ({
  marginTop: theme.space[5],
  background: theme.colors.white,
})

export type PlanTierCardProps = {
  tier: TierInformation
  tierType: TierType
  onSelect: (value: TierInformation) => void
  billingInterval: BillingInterval
  detailsLink?: string
  isSelected?: boolean
  contactSalesLink?: string
  enterpriseSelectable?: boolean
} & Pick<
  AriaFormGroupFieldData,
  "getOptionControlProps" | "getOptionLabelProps"
>

export function PlanTierCard({
  tier,
  tierType,
  isSelected,
  onSelect,
  getOptionControlProps,
  getOptionLabelProps,
  detailsLink,
  billingInterval,
  contactSalesLink,
  enterpriseSelectable,
}: PlanTierCardProps) {
  const { trackSegment } = useTracker()
  const optionValue = tier.pricingTier
  const colorCss = tier.color
  const isStaticCard =
    tier.pricingTier === MachinePricingTier.Enterprise && !enterpriseSelectable

  const description =
    tierType === `builds` ? tier.buildsDescription : tier.hostingDescription
  const featuresIntro =
    tierType === `builds` ? tier.buildsFeaturesIntro : tier.hostingFeaturesIntro
  const features =
    tierType === `builds` ? tier.buildsFeatures : tier.hostingFeatures
  const featuresSectionId = `features_${tier.key}`
  const tierIcon = tierType === `builds` ? tier.buildsIcon : tier.hostingIcon

  const amount =
    tierType === `builds` ? tier.buildsMonthlyAmount : tier.hostingMonthlyAmount
  const intervalAmount = amount && amount[billingInterval]
  const roundedAmountInUSD =
    intervalAmount && intervalAmount !== null
      ? Math.round(intervalAmount / 100)
      : 0

  return (
    <RadioButtonCard
      key={`${tierType}TierOption_${tier.key}`}
      checked={isSelected}
      css={(theme: Theme) => [
        rootCss(theme),
        isSelected && isSelectedCss(theme),
        !isSelected && isHoveredCss(theme),
        isStaticCard && isStaticCss(theme),
      ]}
    >
      <div
        css={theme => [headerCss(theme), isStaticCard && isCenteredCss(theme)]}
      >
        <span css={pileCss}>
          {!isStaticCard && (
            <RadioButtonCardInput
              value={optionValue}
              name={`${tierType}TierOption`}
              checked={isSelected}
              onChange={() => {
                onSelect(tier)
              }}
              aria-describedby={featuresSectionId}
              {...getOptionControlProps(optionValue)}
            />
          )}
          <TierIcon icon={tierIcon} isStaticCard={isStaticCard} />
        </span>
        <label
          {...getOptionLabelProps(optionValue)}
          css={(theme: Theme) => [labelCss(theme), { color: colorCss }]}
        >
          {tier.name}
        </label>
        {intervalAmount !== null && (
          <div css={pricingCss}>
            $<b>{roundedAmountInUSD}</b>/
            <small>{billingText.morphemes.month}</small>
          </div>
        )}
      </div>

      {description && (
        <Text size="S" css={(theme: Theme) => [descriptionCss(theme)]}>
          {description}
        </Text>
      )}

      {featuresIntro && (
        <Text
          css={theme => [
            featuresIntroCss(theme),
            isStaticCard && {
              textAlign: `center`,
            },
          ]}
        >
          {featuresIntro}
        </Text>
      )}

      {features && (
        <ul
          css={theme => [
            featuresList(theme),
            isStaticCard && isCenteredCss(theme),
            enterpriseSelectable && {
              marginTop: 0,
              marginBottom: `auto`,
            },
          ]}
        >
          {features.map((feature: RichFeature, idx: number) => {
            const { label, icon: Icon, negative } = feature

            return (
              <li key={idx} css={featureItemCss}>
                <span css={featureIconBox}>
                  {Icon ? (
                    <Icon aria-hidden size="xsmall" css={featureIconCss} />
                  ) : (
                    <CheckIcon css={checkIconCss} size="small" />
                  )}
                </span>{" "}
                <span
                  css={
                    negative && {
                      textDecoration: `line-through`,
                    }
                  }
                >
                  {label}
                </span>
              </li>
            )
          })}
        </ul>
      )}

      {detailsLink && (
        <a
          href={detailsLink}
          css={theme => [
            detailsLinkCss(theme),
            isStaticCard && {
              marginLeft: 0,
              alignSelf: `center`,
            },
          ]}
        >
          see all features <MdArrowForward />
        </a>
      )}

      {isStaticCard && contactSalesLink && (
        <LinkButton
          to={contactSalesLink}
          size="L"
          variant="SECONDARY"
          css={contactSalesCss}
          onClick={e => {
            e.preventDefault()

            trackSegment({
              type: SegmentEventType.Track,
              event: "Clicked Contact Sales",
              properties: {
                // we use Contact Sales buttons only at Pricing page so the location value could be hardcoded for now
                location: "Pricing Page",
                plan: capitalizeStr({ str: tier.key.toLowerCase() }),
              },
            })

            navigate(contactSalesLink)
          }}
        >
          Contact Sales
        </LinkButton>
      )}
    </RadioButtonCard>
  )
}
